import { useRef } from "react";
import { API } from "@/lib/api";

/**
 * Top toolbar of the Screenplay editor.
 * All persistence/save state, fullscreen flag, screenplay metadata and the
 * import handler are owned by the parent.
 */
export default function ScreenplayToolbar({
  projectId,
  title,
  language,
  saving,
  savedAt,
  stats,
  fullscreen,
  onToggleFullscreen,
  onChangeTitle,
  onChangeLanguage,
  onImportFdx,
  onOpenSpellcheck,
  onOpenRename,
  onCleanEmpty,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) {
  const importInputRef = useRef(null);
  const onClickImportFdx = () => importInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await onImportFdx?.(file);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-stone-300/70 bg-[#F1EEE5]/60 px-4 py-3"
      data-testid="screenplay-toolbar"
    >
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleFullscreen}
          data-testid="screenplay-fullscreen-toggle"
          title={fullscreen ? "Salir de pantalla completa (Esc)" : "Pantalla completa"}
          className="rounded-full border border-stone-400 px-3 py-1.5 text-[11px] uppercase tracking-widest text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E]"
        >
          {fullscreen ? "↙ Salir" : "⛶ Pantalla completa"}
        </button>
        <input
          value={title || ""}
          onChange={(e) => onChangeTitle?.(e.target.value)}
          placeholder="Título del guion"
          data-testid="screenplay-title-input"
          className="rounded border border-stone-300 bg-white px-3 py-1.5 font-serif text-sm outline-none focus:border-[#26775E]"
          style={{ minWidth: 260 }}
        />
        <select
          value={language || "es"}
          onChange={(e) => onChangeLanguage?.(e.target.value)}
          data-testid="screenplay-lang-select"
          className="rounded border border-stone-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#26775E]"
          title="Idioma del guion"
        >
          <option value="es">ES</option>
          <option value="en">EN</option>
        </select>
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest text-stone-500" data-testid="screenplay-stats">
          {stats.scenes} escenas · {stats.words} palabras · {stats.characters} personajes
        </span>
        <span className="text-[11px] text-stone-500" data-testid="screenplay-save-status">
          {saving ? "Guardando…" : savedAt ? "Guardado ✓" : ""}
        </span>
        <a
          href={`${API}/icaa/projects/${projectId}/screenplay/export/pdf`}
          data-testid="screenplay-export-pdf"
          className="rounded-full bg-[#26775E] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white transition hover:bg-stone-900"
        >
          ↓ PDF
        </a>
        <a
          href={`${API}/icaa/projects/${projectId}/screenplay/export/fdx`}
          data-testid="screenplay-export-fdx"
          className="rounded-full border border-stone-400 px-3 py-1.5 text-[11px] uppercase tracking-widest text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E]"
        >
          ↓ .FDX
        </a>
        <button
          onClick={onClickImportFdx}
          data-testid="screenplay-import-fdx-btn"
          className="rounded-full border border-stone-400 px-3 py-1.5 text-[11px] uppercase tracking-widest text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E]"
        >
          ↑ Importar .FDX
        </button>
        <input
          ref={importInputRef}
          type="file"
          accept=".fdx,application/xml,text/xml"
          onChange={handleFile}
          className="hidden"
          data-testid="screenplay-import-input"
        />

        {/* Undo / Redo a nivel de bloques (rescata cortes, borrados, pegados…) */}
        <div className="ml-1 flex items-center gap-1 border-l border-stone-300/70 pl-3" data-testid="screenplay-undo-redo">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            data-testid="sp-tool-undo"
            title="Deshacer última operación de bloque (⌘/Ctrl + Z)"
            className="rounded-full border border-stone-400 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-stone-400 disabled:hover:text-stone-700"
          >
            ↶
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            data-testid="sp-tool-redo"
            title="Rehacer (⌘/Ctrl + Shift + Z)"
            className="rounded-full border border-stone-400 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-stone-400 disabled:hover:text-stone-700"
          >
            ↷
          </button>
        </div>

        {/* Herramientas (Ortografía + Renombrar) */}
        <div className="ml-1 flex items-center gap-1.5 border-l border-stone-300/70 pl-3" data-testid="screenplay-tools">
          <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-stone-500">Herramientas</span>
          <button
            onClick={onOpenSpellcheck}
            data-testid="sp-tool-spellcheck"
            title="Revisar ortografía con IA"
            className="rounded-full border border-stone-400 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 transition hover:border-[#941620] hover:text-[#941620]"
          >
            🔤 Ortografía
          </button>
          <button
            onClick={onOpenRename}
            data-testid="sp-tool-rename"
            title="Renombrar un personaje en TODO el guion"
            className="rounded-full border border-stone-400 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 transition hover:border-[#941620] hover:text-[#941620]"
          >
            🔄 Renombrar
          </button>
          <button
            onClick={onCleanEmpty}
            data-testid="sp-tool-clean-empty"
            title="Eliminar bloques vacíos que crean huecos en el guion (no afecta al PDF, pero limpia el editor)"
            className="rounded-full border border-stone-400 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-stone-700 transition hover:border-[#941620] hover:text-[#941620]"
          >
            🧹 Limpiar huecos
          </button>
        </div>
      </div>
    </div>
  );
}
